import { createMiddleware, createServerFn } from '@tanstack/react-start'
import { getRequestHeader } from '@tanstack/react-start/server'
import { getAccessToken } from '~/lib/access-token'
import {
  rpcAuthMiddleware,
  runClientWithRecovery,
  runServerWithUnauthorizedTag,
} from './rpc-auth-middleware'
import { verifyAccessTokenForBootstrap } from './rpc-auth'
import {
  deleteUserHandler,
  getCurrentUserHandler,
  listUsersHandler,
  syncCurrentUserHandler,
  updateUserRoleHandler,
  type SyncCurrentUserInput,
} from './users.handlers'

/**
 * Bootstrap auth middleware for `syncCurrentUser` only.
 *
 * Differs from `rpcAuthMiddleware` in one critical way: the server step does
 * NOT require a `users` row to exist. `syncCurrentUser` is the row's *insert*
 * path; gating it on row existence would make first sign-in unreachable. The
 * middleware verifies the JWT against Keycloak's JWKS and exposes the verified
 * `sub`/`email`/`name` claims as `context.subject` — authoritative identity,
 * ignoring whatever the request body claims.
 */
const bootstrapAuthMiddleware = createMiddleware({ type: 'function' })
  .client(async ({ next }) => {
    const token = getAccessToken()
    return runClientWithRecovery(() =>
      next({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    )
  })
  .server(async ({ next }) =>
    runServerWithUnauthorizedTag(async () => {
      const header = getRequestHeader('authorization')
      const subject = await verifyAccessTokenForBootstrap(header)
      return next({ context: { subject } })
    }),
  )

// RPC wrappers — thin delegates to the handlers in ./users.handlers. The
// handlers live in a sibling file the UI doesn't import so their
// `@rapid-response/db` usage stays out of the client bundle.

export const syncCurrentUser = createServerFn({ method: 'POST' })
  .middleware([bootstrapAuthMiddleware])
  .inputValidator((d: SyncCurrentUserInput) => d)
  .handler(async ({ data, context }) => syncCurrentUserHandler(context.subject, data))

export const getCurrentUser = createServerFn({ method: 'GET' })
  .middleware([rpcAuthMiddleware])
  .handler(async ({ context }) => getCurrentUserHandler(context.caller))

export const listUsers = createServerFn({ method: 'GET' })
  .middleware([rpcAuthMiddleware])
  .handler(async ({ context }) => listUsersHandler({ caller: context.caller }))

export const updateUserRole = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator((d: { userId: string; roleId: string }) => d)
  .handler(async ({ data, context }) =>
    updateUserRoleHandler({ ...data, caller: context.caller }),
  )

export const deleteUser = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator((d: { userId: string }) => d)
  .handler(async ({ data, context }) =>
    deleteUserHandler({ ...data, caller: context.caller }),
  )
